/**
 * useMinhaMesaRibbon — Ribbon config for Minha Mesa (MasterRibbon pattern)
 *
 * Groups:
 *  "Fila"          (left) : [Puxar Próxima]  [Arquivo ▾]
 *  "Visualização"  (center): [Lista] [Kanban] [Timeline]
 *
 * Arquivo ▾ dropdown:
 *   Histórico de Conclusões
 *   ─────────────────
 *   Relatório de Produtividade
 *
 * slots.afterGroups → filtersWidget (Período ▾, Tipo ▾, Ordenar ▾, Busca)
 */
import { useMemo } from 'react';
import {
  Play, Folder, ClipboardList, BarChart3,
  List, LayoutGrid, GanttChart,
  Zap, AlertTriangle, Clock, Plus,
} from 'lucide-react';
import type { RibbonConfig } from '../../../types/ribbon';

const NOOP = () => {};

export type MinhaMesaViewMode = 'list' | 'kanban' | 'timeline';
export type QueueSortMode = 'score' | 'risk' | 'tp';

export interface MinhaMesaRibbonDeps {
  canPullNext?: boolean;
  cockpitActive?: boolean;
  onPullNextTask?: () => void;
  /** Abre a Gaveta Retrátil de Nova Solicitação (Prompt 31). */
  onOpenLauncher?: () => void;
  workViewMode: MinhaMesaViewMode;
  onSetWorkViewMode: (mode: MinhaMesaViewMode) => void;
  historyActive?: boolean;
  onToggleHistory?: () => void;
  onExportProductivity?: () => void;
  queueSortMode?: QueueSortMode;
  onQueueSortModeChange?: (mode: QueueSortMode) => void;
  filtersWidget?: React.ReactNode;
}

export function useMinhaMesaRibbon(deps: MinhaMesaRibbonDeps): RibbonConfig {
  const showPull = !deps.cockpitActive && !!deps.onPullNextTask && !!deps.canPullNext;

  return useMemo<RibbonConfig>(() => ({
    moduleId: 'minha-mesa',
    groups: [
      // ─── Fila (LEFT) ────────────────────────────
      {
        id: 'fila',
        label: 'Fila',
        position: 'left',
        actions: [
          {
            id: 'nova-solicitacao',
            label: 'Nova Solicitação',
            icon: Plus,
            onClick: deps.onOpenLauncher ?? NOOP,
            variant: 'primary',
            hidden: !deps.onOpenLauncher,
          },
          {
            id: 'pull-next',
            label: 'Iniciar',
            icon: Play,
            onClick: deps.onPullNextTask ?? NOOP,
            variant: 'primary',
            hidden: !showPull,
          },
          {
            id: 'arquivo',
            label: 'Arquivo',
            icon: Folder,
            onClick: NOOP,
            dropdown: [
              {
                id: 'history',
                label: 'Histórico de Conclusões',
                icon: ClipboardList,
                onClick: deps.onToggleHistory ?? NOOP,
              },
              { id: 'div1', label: '', onClick: NOOP, divider: true },
              {
                id: 'export-productivity',
                label: 'Relatório de Produtividade',
                icon: BarChart3,
                onClick: deps.onExportProductivity ?? NOOP,
              },
            ],
          },
        ],
      },
      // ─── Visualização (CENTER) ───────────────────
      {
        id: 'visualizacao',
        label: 'Visualização',
        position: 'center',
        actions: [
          {
            id: 'view-list',
            label: 'Lista',
            icon: List,
            onClick: () => deps.onSetWorkViewMode('list'),
            active: deps.workViewMode === 'list',
          },
          {
            id: 'view-kanban',
            label: 'Kanban',
            icon: LayoutGrid,
            onClick: () => deps.onSetWorkViewMode('kanban'),
            active: deps.workViewMode === 'kanban',
          },
          {
            id: 'view-timeline',
            label: 'Timeline',
            icon: GanttChart,
            onClick: () => deps.onSetWorkViewMode('timeline'),
            active: deps.workViewMode === 'timeline',
          },
        ],
      },
      // ─── Inteligência (CENTER) ───────────────────
      {
        id: 'inteligencia',
        label: 'Inteligência',
        position: 'center',
        actions: [
          {
            id: 'sort-score',
            label: 'Auto',
            icon: Zap,
            onClick: () => deps.onQueueSortModeChange?.('score'),
            active: deps.queueSortMode === 'score',
            tooltip: 'Prioridade Auto: score = (atraso SLA × 3) + (prioridade × 2) − (tempo fila × 0.5)',
          },
          {
            id: 'sort-risk',
            label: 'Risco SLA',
            icon: AlertTriangle,
            onClick: () => deps.onQueueSortModeChange?.('risk'),
            active: deps.queueSortMode === 'risk',
          },
          {
            id: 'sort-tp',
            label: 'Maior TP',
            icon: Clock,
            onClick: () => deps.onQueueSortModeChange?.('tp'),
            active: deps.queueSortMode === 'tp',
          },
        ],
      },
    ],
    slots: {
      afterGroups: deps.filtersWidget,
    },
  }), [deps, showPull]);
}
